// client/src/components/CostEstimator.jsx
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../utils/api';
import { supabase } from '../utils/supabaseClient';
import { quoteCredits } from '../utils/creditApi';
import { listVendorAccounts } from '../utils/vendorApi';
import { ProjectContext } from '../contexts/ProjectContext';
import { useCredits } from '../contexts/CreditContext';
import { toast } from 'react-toastify';

/**
 * Preferred flow: pass the original uploaded file (File/Blob) via `uploadedFile`.
 * If no file is provided, we try a minimal, clean-ASCII CSV built from partsList.
 */
const summarizeProgress = (entries, total) => {
  const list = Array.isArray(entries) ? entries : [];
  let completed = 0;
  for (const entry of list) {
    if (!entry) continue;
    const status = entry.status;
    if (status === 'done' || status === 'error' || status === 'cancelled') {
      completed += 1;
    }
  }
  const targetTotal = Number.isFinite(total) && total > 0 ? total : list.length;
  const percent = targetTotal ? Math.round((completed / targetTotal) * 100) : 0;
  return { completed, percent: Math.min(100, Math.max(0, percent)) };
};

const normalizeQueryKey = (value) => String(value || '').trim().toLowerCase();

const currencyFormatter =
  typeof Intl !== 'undefined'
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
    : null;

const parsePriceValue = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value === null || value === undefined) return null;
  const cleaned = String(value).replace(/[^0-9.,-]/g, '');
  if (!cleaned) return null;
  const normalized = cleaned.replace(/,/g, '');
  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatCurrencyValue = (value) => {
  if (!Number.isFinite(value)) return '';
  if (currencyFormatter) return currencyFormatter.format(value);
  return `$${value.toFixed(2)}`;
};

const slugifyString = (value = '') =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'option';

const isHttpUrl = (value) => typeof value === 'string' && /^https?:\/\//i.test(value.trim());
const sanitizeExternalUrl = (value) => {
  const candidate = typeof value === 'string' ? value.trim() : '';
  if (!isHttpUrl(candidate)) return '';
  try {
    const normalized = new URL(candidate);
    const protocol = normalized.protocol.toLowerCase();
    return protocol === 'http:' || protocol === 'https:' ? normalized.href : '';
  } catch {
    return '';
  }
};

const normalizeCandidateRecord = (candidate = {}, fallbackQuery = '') => {
  const raw = candidate && typeof candidate === 'object' ? candidate : {};
  const query = String(raw.query || fallbackQuery || '').trim();
  const title = String(raw.title || raw.item || raw.name || raw.description || raw.model || '').trim();
  const price = raw.price != null ? String(raw.price).trim() : '';
  const priceValue = parsePriceValue(raw.price);
  const priceDisplay = price || (Number.isFinite(priceValue) ? formatCurrencyValue(priceValue) : '');
  const source = String(raw.source || raw.vendor || raw.supplier || raw.brand || '').trim();
  const rawProductLink = String(raw.product_link || raw.url || raw.link || raw.href || '').trim();
  const rawUrl = String(raw.url || rawProductLink || '').trim();
  const url = sanitizeExternalUrl(rawUrl);
  const productLink = sanitizeExternalUrl(rawProductLink) || url;
  const vendor = String(raw.vendor || raw.source || '').trim();
  const compareKey = (productLink || url || `${title}|${source}|${price}`).toLowerCase();
  return {
    query,
    title,
    price,
    priceValue,
    priceDisplay,
    source,
    product_link: productLink || url || '',
    url,
    vendor,
    raw,
    compareKey,
  };
};

const generateLaborEntryId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `labor-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

function normalizeLaborEntries(entries = [], fallbackBreakdown = []) {
  if (Array.isArray(entries) && entries.length) {
    return entries.map((entry) => ({
      id: entry?.id || generateLaborEntryId(),
      roleId: entry?.roleId ?? entry?.laborRoleId ?? null,
      hours: Number(entry?.hours ?? entry?.laborHours ?? 0) || 0,
      notes: entry?.notes || '',
      rateOverride: entry?.rateOverride ?? entry?.laborRate ?? null,
    }));
  }

  const migrated = new Map();
  (fallbackBreakdown || []).forEach((row) => {
    const roleId = row?.laborRoleId || null;
    const hours = Number(row?.laborHours || 0);
    if (!roleId || !hours) return;
    const current = migrated.get(roleId) || { roleId, hours: 0 };
    current.hours += hours;
    migrated.set(roleId, current);
  });

  return Array.from(migrated.values()).map((entry) => ({
    id: generateLaborEntryId(),
    roleId: entry.roleId,
    hours: entry.hours,
    notes: '',
    rateOverride: null,
  }));
}

export default function CostEstimator({
  partsList,
  tableLabel,
  initialResult,
  uploadedFile,
  manualTrigger = 0,
  manualMode = false,
  laborProfiles = [],
  laborEnabled = false,
  laborLoading = false,
  laborError = '',
}) {
  const { activeProject } = useContext(ProjectContext);

  const { balance: creditBalance, loading: creditsLoading, loaded: creditsLoaded, refreshBalance, setBalance } = useCredits();

  const [editableItems, setEditableItems] = useState(null);
  const [result, setResult]           = useState(initialResult || null);
  // removed CSV Analysis box per request
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [confirming, setConfirming]   = useState(false);
  const [error, setError]             = useState('');
  const [pricingProgress, setPricingProgress] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedOptions, setSelectedOptions] = useState({});
  const [optionsRevision, setOptionsRevision] = useState(0);
  const [globalPrompt, setGlobalPrompt] = useState('');
  const [globalVendors, setGlobalVendors] = useState([]);
  const [globalVendorInput, setGlobalVendorInput] = useState('');
  const [vendorSearchMode, setVendorSearchMode] = useState('live'); // live | database | both
  const [zipCode, setZipCode] = useState('');
  const [hasExtracted, setHasExtracted] = useState(false);
  const [laborEntries, setLaborEntries] = useState(() =>
    normalizeLaborEntries(initialResult?.laborEntries, initialResult?.breakdown)
  );

  const [creditQuote, setCreditQuote] = useState({
    totalCost: 0,
    available: creditBalance?.available ?? 0,
    shortfall: 0,
    loading: false,
    breakdown: [],
    error: ''
  });
  const creditRequestRef = useRef('');
  const [cancelling, setCancelling] = useState(false);
  const [activeRequestId, setActiveRequestId] = useState('');
  const [editState, setEditState] = useState(null);
  const [inlineLoadingIndex, setInlineLoadingIndex] = useState(null);
  const [vendorAccounts, setVendorAccounts] = useState([]);
  const [vendorAccountsLoading, setVendorAccountsLoading] = useState(false);
  const [selectedVendorAccountIds, setSelectedVendorAccountIds] = useState([]);
  const [vendorDbMenuOpen, setVendorDbMenuOpen] = useState(false);
  const vendorDbBtnRef = useRef(null);
  const vendorDbMenuRef = useRef(null);
  const vendorAccountNameMap = useMemo(() => {
    const map = new Map();
    vendorAccounts.forEach((acct) => {
      map.set(acct.id, acct.name);
    });
    return map;
  }, [vendorAccounts]);
  useEffect(() => {
    setLaborEntries(normalizeLaborEntries(initialResult?.laborEntries, initialResult?.breakdown));
  }, [initialResult]);

  const laborProfileMap = useMemo(() => {
    const map = new Map();
    (laborProfiles || []).forEach((profile) => {
      if (profile?.id) {
        map.set(profile.id, profile);
      }
    });
    return map;
  }, [laborProfiles]);

  const hasLaborFeature = Boolean(laborEnabled);
  const hasLaborProfiles = Boolean(laborProfiles?.length);

  const laborEntriesDetailed = useMemo(
    () =>
      laborEntries.map((entry) => {
        const profile = entry.roleId ? laborProfileMap.get(entry.roleId) : null;
        const rate = Number(profile?.hourly_rate ?? entry.rateOverride ?? 0) || 0;
        const hours = Number(entry.hours) || 0;
        return {
          ...entry,
          name: profile?.name || entry.name || 'Labor',
          jobTitle: profile?.job_title || entry.jobTitle || '',
          rate,
          hours,
          cost: rate * hours,
        };
      }),
    [laborEntries, laborProfileMap]
  );

  useEffect(() => {
    let cancelled = false;
    const fetchVendors = async () => {
      setVendorAccountsLoading(true);
      try {
        const data = await listVendorAccounts();
        if (!cancelled) {
          setVendorAccounts(data || []);
        }
      } catch (err) {
        console.error(err);
        // Suppress toast if simply empty/missing; show only hard errors with message
        if (!cancelled && (err?.response?.data?.error || err?.message)) {
          toast.error(err?.response?.data?.error || 'Failed to load vendor DBs');
        }
      } finally {
        if (!cancelled) setVendorAccountsLoading(false);
      }
    };
    fetchVendors();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!vendorDbMenuOpen) return;
    const handleClick = (e) => {
      if (
        vendorDbBtnRef.current?.contains(e.target) ||
        vendorDbMenuRef.current?.contains(e.target)
      ) {
        return;
      }
      setVendorDbMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [vendorDbMenuOpen]);

  const laborTotals = useMemo(
    () =>
      laborEntriesDetailed.reduce(
        (acc, entry) => {
          acc.hours += entry.hours;
          acc.cost += entry.cost;
          return acc;
        },
        { hours: 0, cost: 0 }
      ),
    [laborEntriesDetailed]
  );

  const addLaborEntry = useCallback(() => {
    setLaborEntries((prev) => [
      ...prev,
      {
        id: generateLaborEntryId(),
        roleId: hasLaborProfiles ? (laborProfiles?.[0]?.id ?? null) : null,
        hours: 0,
        notes: '',
        rateOverride: null,
      },
    ]);
  }, [hasLaborProfiles, laborProfiles]);

  const updateLaborEntry = useCallback((entryId, patch) => {
    setLaborEntries((prev) => prev.map((entry) => (entry.id === entryId ? { ...entry, ...patch } : entry)));
  }, []);

  const removeLaborEntry = useCallback((entryId) => {
    setLaborEntries((prev) => prev.filter((entry) => entry.id !== entryId));
  }, []);

  const clearResultState = useCallback(() => {
    setResult(null);
    setLaborEntries([]);
  }, []);

  useEffect(() => {
    setHasExtracted(false);
  }, [uploadedFile]);

  useEffect(() => {
    if (!result) {
      setExpandedRows({});
      setSelectedOptions({});
    }
  }, [result]);
  useEffect(() => {
    if (!result) {
      setEditState(null);
      setInlineLoadingIndex(null);
    }
  }, [result]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!document.getElementById('pricing-loading-bar-style')) {
      const styleEl = document.createElement('style');
      styleEl.id = 'pricing-loading-bar-style';
      styleEl.textContent = `@keyframes pricing-loading-bar { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }`;
      document.head.appendChild(styleEl);
    }
  }, []);

  useEffect(() => {
    if (!initialResult || !Array.isArray(initialResult.breakdown)) return;
    const normalized = initialResult.breakdown.map((row) => {
      const totalItemCost = Number(row.totalItemCost) || 0;
      return {
        ...row,
        label: row.label || row.item || '',
        totalItemCost,
        lineTotal: totalItemCost,
      };
    });
    const totals = computeTotalsForRows(normalized);
    setResult({
      breakdown: normalized,
      materialTotal: totals.material,
      totalCost: totals.material,
    });
  }, [initialResult, computeTotalsForRows]);

  const payloadRef = useRef([]);
  const pricingRowsRef = useRef([]);
  const aggregatedRef = useRef(new Map());
  const controllersRef = useRef([]);
  const optionsCacheRef = useRef(new Map());
  const reattachPollRef = useRef({ stop: () => {} });


  const recomputeBreakdown = React.useCallback(() => {
    const rows = pricingRowsRef.current || [];
    if (!rows.length) {
      clearResultState();
      return;
    }

    const idx = new Map();
    aggregatedRef.current.forEach((value, key) => {
      if (!key) return;
      idx.set(key, value);
    });

    const breakdown = rows.map((row) => {
      const key = (row._query || '').toLowerCase();
      const fallbackKey = (row.item || '').toLowerCase();
      const match = idx.get(key) || idx.get(fallbackKey) || {};
      const unitCost = parseFloat(String(match.price ?? '').replace(/[^0-9.]/g, '')) || 0;
      const quantity = toPositiveQuantity(row.quantity);
      const totalItemCost = unitCost * quantity;
      const matchSource =
        match.vendor ||
        match.source ||
        (match.vendor_account_id ? vendorAccountNameMap.get(match.vendor_account_id) : null);
      return {
        item: row._query,
        label: row.label || row.item,
        quantity,
        unitCost,
        totalItemCost,
        product_link: match.product_link || match.url || null,
        source: matchSource || null,
        refunded: Boolean(match.refunded),
        refundReason: match.refundReason || null,
        lineTotal: totalItemCost,
      };
    });

    const totals = computeTotalsForRows(breakdown);
    setResult({
      breakdown,
      materialTotal: totals.material,
      totalCost: totals.material,
    });
  }, [clearResultState, computeTotalsForRows]);

  const buildCandidateList = useCallback((rawCandidates, querySource) => {
    const normalizedCandidates = [];
    const seen = new Set();
    (Array.isArray(rawCandidates) ? rawCandidates : []).forEach((candidate) => {
      const normalized = normalizeCandidateRecord(candidate, querySource);
      const dedupeKey = normalized.compareKey || normalized.title || normalized.product_link || normalized.url;
      if (dedupeKey) {
        if (seen.has(dedupeKey)) return;
        seen.add(dedupeKey);
      }
      // Hydrate global/client prompts for downstream display/debug
      normalizedCandidates.push({
        ...normalized,
        client_prompt: candidate.client_prompt || candidate.clientPrompt || null,
        global_prompt: candidate.global_prompt || candidate.globalPrompt || null,
      });
    });
    return normalizedCandidates;
  }, []);

  const updateOptionsCache = useCallback((querySource, rawCandidates) => {
    const key = normalizeQueryKey(querySource);
    if (!key) return;

    const normalizedCandidates = buildCandidateList(rawCandidates, querySource);
    const aggregated = aggregatedRef.current.get(key);

    const deduped = [];
    const seen = new Set();
    const pushCandidate = (candidate) => {
      if (!candidate) return;
      const compareKey =
        candidate.compareKey ||
        candidate.product_link ||
        candidate.url ||
        `${candidate.title}|${candidate.source}|${candidate.price}`;
      const finalKey = compareKey ? compareKey.toLowerCase() : '';
      if (finalKey && seen.has(finalKey)) return;
      seen.add(finalKey || `idx-${deduped.length}`);
      deduped.push(candidate);
    };

    if (aggregated) {
      pushCandidate(normalizeCandidateRecord(aggregated, querySource));
    }
    normalizedCandidates.forEach(pushCandidate);

    if (!deduped.length) return;

    const fingerprint = deduped
      .map((c) => c.compareKey || `${c.title}|${c.source}|${c.price}`)
      .join('||');
    const existing = optionsCacheRef.current.get(key);
    const existingFingerprint = existing?.candidates
      ? existing.candidates.map((c) => c.compareKey || `${c.title}|${c.source}|${c.price}`).join('||')
      : '';
    if (existing && existingFingerprint === fingerprint && existing.query === querySource) {
      return;
    }
    optionsCacheRef.current.set(key, { query: querySource, candidates: deduped });
    setOptionsRevision((prev) => prev + 1);
  }, [buildCandidateList]);

  const dedupeCandidates = useCallback((candidates) => {
    const unique = [];
    const seen = new Set();
    for (const candidate of candidates || []) {
      if (!candidate) continue;
      const key =
        (candidate.compareKey ||
          candidate.product_link ||
          candidate.url ||
          `${candidate.title}|${candidate.source}|${candidate.price}` ||
          '')?.toLowerCase();
      if (key && seen.has(key)) continue;
      if (key) seen.add(key);
      unique.push(candidate);
    }
    return unique;
  }, []);

  const padOptionsWithSyntheticAlternatives = useCallback(
    (candidates, _row, desiredCount = 3) => {
      const target = Math.max(1, desiredCount);
      return dedupeCandidates(candidates).slice(0, target);
    },
    [dedupeCandidates]
  );

  const resolveQuerySource = (evtQuery, idx) => {
    if (evtQuery && typeof evtQuery === 'string' && evtQuery.trim()) {
      return evtQuery;
    }
    if (Number.isInteger(idx)) {
      const payloadItem = Array.isArray(payloadRef.current) ? payloadRef.current[idx] : null;
      if (payloadItem?.query) return payloadItem.query;
      const row = Array.isArray(pricingRowsRef.current) ? pricingRowsRef.current[idx] : null;
      if (row?._query) return row._query;
      if (row?.item) return row.item;
    }
    return '';
  };

  const optionsByQuery = useMemo(() => {
    const map = new Map();
    const registerBucket = (bucket) => {
      if (!bucket) return;
      const candidates = (Array.isArray(bucket.candidates) ? bucket.candidates : []).filter(Boolean);
      if (!candidates.length) return;
      const bucketData = {
        query: bucket.query || '',
        candidates,
        entry: bucket.entry || null,
      };
      const keys = new Set();
      if (bucketData.query) {
        keys.add(normalizeQueryKey(bucketData.query));
      }
      candidates.forEach((candidate) => {
        const queryKey = normalizeQueryKey(candidate.query);
        if (queryKey) keys.add(queryKey);
        const titleKey = normalizeQueryKey(candidate.title);
        if (titleKey) keys.add(titleKey);
      });
      keys.forEach((key) => {
        if (!key) return;
        const existing = map.get(key);
        if (!existing || (bucketData.candidates.length > (existing.candidates?.length || 0))) {
          map.set(key, bucketData);
        }
      });
    };

    const entries = Array.isArray(pricingProgress?.entries) ? pricingProgress.entries : [];
    if (entries.length) {
      entries.forEach((entry, idx) => {
        const querySource = resolveQuerySource(entry?.query, idx) || entry?.label || entry?.query || '';
        const normalizedKey = normalizeQueryKey(querySource);
        const normalizedCandidates = buildCandidateList(entry?.results || [], querySource);
        const candidates = [...normalizedCandidates];
        if (normalizedKey) {
          const aggregated = aggregatedRef.current.get(normalizedKey);
          if (aggregated) {
            const normalizedAggregated = normalizeCandidateRecord(aggregated, querySource);
            const dedupeKey =
              normalizedAggregated.compareKey ||
              normalizedAggregated.product_link ||
              normalizedAggregated.url ||
              `${normalizedAggregated.title}|${normalizedAggregated.source}|${normalizedAggregated.price}`;
            const seen = new Set(
              candidates
                .map((c) => c.compareKey || c.product_link || c.url || `${c.title}|${c.source}|${c.price}`)
                .map((value) => (value ? value.toLowerCase() : ''))
            );
            const lowered = dedupeKey ? dedupeKey.toLowerCase() : '';
            if ((lowered && !seen.has(lowered)) || !dedupeKey) {
              candidates.unshift(normalizedAggregated);
            }
          }
        }
        if (!candidates.length && normalizedKey) {
          const aggregated = aggregatedRef.current.get(normalizedKey);
          if (aggregated) {
            candidates.push(normalizeCandidateRecord(aggregated, querySource));
          }
        }
        registerBucket({ query: querySource, candidates, entry });
      });
    }

    optionsCacheRef.current.forEach((bucket) => {
      registerBucket(bucket);
    });

    aggregatedRef.current.forEach((value, key) => {
      const querySource = value?.query || key;
      const candidate = normalizeCandidateRecord(value, querySource);
      registerBucket({ query: querySource, candidates: [candidate] });
    });

    return map;
  }, [pricingProgress, optionsRevision, buildCandidateList]);

  const getSelectedIndex = useCallback((key, candidates, fallbackQuery) => {
    if (!Array.isArray(candidates) || !candidates.length) return -1;
    const manualIndex = selectedOptions[key];
    if (Number.isInteger(manualIndex) && manualIndex >= 0 && manualIndex < candidates.length) {
      return manualIndex;
    }
    const aggregated = aggregatedRef.current.get(key);
    if (aggregated) {
      const normalizedSelected = normalizeCandidateRecord(aggregated, fallbackQuery);
      if (normalizedSelected.compareKey) {
        const matchIdx = candidates.findIndex((candidate) => candidate.compareKey === normalizedSelected.compareKey);
        if (matchIdx !== -1) return matchIdx;
      }
      const matchIdx = candidates.findIndex((candidate) => {
        if (candidate.product_link && aggregated.product_link && candidate.product_link === aggregated.product_link) return true;
        if (candidate.url && (candidate.url === aggregated.url || candidate.url === aggregated.product_link)) return true;
        return false;
      });
      if (matchIdx !== -1) return matchIdx;
    }
    return 0;
  }, [selectedOptions]);

  const findOptionsBucket = useCallback((itemName) => {
    const primaryKey = normalizeQueryKey(itemName);
    if (primaryKey && optionsByQuery.has(primaryKey)) {
      return { bucket: optionsByQuery.get(primaryKey), key: primaryKey };
    }
    if (primaryKey) {
      for (const [mapKey, bucket] of optionsByQuery.entries()) {
        const candidates = bucket?.candidates || [];
        for (const candidate of candidates) {
          const candidateQueryKey = normalizeQueryKey(candidate?.query);
          const candidateTitleKey = normalizeQueryKey(candidate?.title);
          if (
            (candidateQueryKey && candidateQueryKey === primaryKey) ||
            (candidateTitleKey && candidateTitleKey === primaryKey)
          ) {
            return { bucket, key: mapKey };
          }
        }
      }
    }
    return { bucket: null, key: primaryKey };
  }, [optionsByQuery]);

  const toggleRowOptions = useCallback((key) => {
    if (!key) return;
    setExpandedRows((prev) => ({
      ...prev,
      [key]: !prev?.[key],
    }));
  }, []);

  const handleOptionSelect = useCallback(
    (key, displayQuery, option, index) => {
      if (!key || !option) return;
      const normalizedKey = normalizeQueryKey(key);
      const storageKey = normalizeQueryKey(displayQuery || key);
      setSelectedOptions((prev) => ({
        ...prev,
        [normalizedKey]: index,
      }));
      const rawCandidate =
        option && typeof option.raw === 'object' && option.raw !== null ? option.raw : option;
      const toStore = { ...rawCandidate };
      if (!toStore.query) {
        toStore.query = displayQuery || key;
      }
      if (toStore.price === undefined || toStore.price === null || String(toStore.price).trim() === '') {
        if (Number.isFinite(option.priceValue)) {
          toStore.price = option.priceValue;
        } else if (option.priceDisplay) {
          toStore.price = option.priceDisplay;
        }
      }
      if (!toStore.source && option.source) {
        toStore.source = option.source;
      }
      if (!toStore.vendor && option.vendor) {
        toStore.vendor = option.vendor;
      }
      if (!toStore.product_link && option.product_link) {
        toStore.product_link = option.product_link;
      }
      if (!toStore.url && option.url) {
        toStore.url = option.url;
      }
      aggregatedRef.current.set(storageKey || normalizedKey, toStore);
      setOptionsRevision((prev) => prev + 1);
      recomputeBreakdown();
    },
    [recomputeBreakdown]
  );

  const upsertResult = React.useCallback((result) => {
    if (!result) return;
    const key = String(result.query || '').toLowerCase();
    if (!key) return;
    aggregatedRef.current.set(key, result);
    recomputeBreakdown();
  }, [recomputeBreakdown]);

  const nextAllowedConfirm = useRef(0);
  const COOLDOWN_MS = 5000;

  const generateCreditRequestId = () => {
    if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
    return `estimate-${Date.now()}`;
  };

  // Autosave helpers: persist pending estimate so a refresh can reattach
  const PENDING_KEY = 'estimator_pending_request_v1';
  const savePendingEstimate = (obj) => {
    try {
      localStorage.setItem(PENDING_KEY, JSON.stringify(obj));
    } catch (err) {
      /* ignore */
    }
  };
  const loadPendingEstimate = () => {
    try {
      const raw = localStorage.getItem(PENDING_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (err) {
      return null;
    }
  };
  const clearPendingEstimate = () => {
    try { localStorage.removeItem(PENDING_KEY); } catch (err) { /* ignore */ }
  };

  // ---------------- Vendors (NEW) ----------------
  // Candidate vendors (same names you see in server logs)
  const ALL_VENDORS = [
    'adasignfactory','belson','compliancesigns','ebay','fergusonhome','hdsupplysolutions',
    'homedepot','homesbyames','inprocorp','madrax','mailboxworks','quill',
    'thecornerguardstore','totalrestroom','walmart','webstaurantstore'
  ];
  const [vendorMenuOpen, setVendorMenuOpen] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState([]); // array of strings
  const [vendorQuery, setVendorQuery] = useState('');
  const vendorBtnRef = useRef(null);
  const vendorMenuRef = useRef(null);
  const [vendorMenuPos, setVendorMenuPos] = useState({ top: 0, left: 0 });
  const showVendorFilter = false;

  useEffect(() => {
    if (!showVendorFilter || !vendorMenuOpen) return;
    const MENU_W = 352; // ~22rem

    const place = () => {
      const btn = vendorBtnRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      let left = Math.min(r.left, window.innerWidth - MENU_W - 8);
      left = Math.max(8, left);
      let top = r.bottom + 8;
      setVendorMenuPos({ top, left });
      // adjust if overflows bottom
      requestAnimationFrame(() => {
        const menu = vendorMenuRef.current;
        if (!menu) return;
        const mh = menu.offsetHeight || 0;
        if (top + mh > window.innerHeight - 8) {
          const above = Math.max(8, r.top - mh - 8);
          setVendorMenuPos({ top: above, left });
        }
      });
    };

    place();
    const onDoc = (e) => {
      const m = vendorMenuRef.current, b = vendorBtnRef.current;
      if (m && m.contains(e.target)) return;
      if (b && b.contains(e.target)) return;
      setVendorMenuOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setVendorMenuOpen(false); };
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [showVendorFilter, vendorMenuOpen]);

  const toggleVendor = (v) => {
    setSelectedVendors(prev =>
      prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]
    );
  };

  // ---------------- UI tokens ----------------
  const card = 'glass-card';
  const input =
    'glass-input w-full text-sm';
  const btnPrimary =
    'glass-cta text-sm disabled:opacity-60 disabled:cursor-not-allowed';
  const btnOutline =
    'glass-ghost text-sm';
  const tableWrap = 'overflow-x-auto rounded-xl border border-white/10 glass-panel';
  const thead =
    'bg-white/5 text-slate-400 text-xs uppercase tracking-wide';
  const cell = 'px-3 py-2';

  // ---------------- row helpers ----------------
  const addRow = () => {
    setEditableItems((prev) => ([...(prev || []), createEditableRow({})]));
  };
  const beginManualEntry = () => {
    setError('');
    clearResultState();
    setPricingProgress(null);
    payloadRef.current = [];
    pricingRowsRef.current = [];
    aggregatedRef.current = new Map();
    optionsCacheRef.current = new Map();
    setOptionsRevision((prev) => prev + 1);
    setEditableItems((prev) => {
      if (Array.isArray(prev) && prev.length) return prev;
      return [createEditableRow({})];
    });
  };
  useEffect(() => {
    if (manualTrigger > 0) {
      beginManualEntry();
    }
  }, [manualTrigger]);

  const removeRow = (idx) => {
    setEditableItems((prev) => (prev ? prev.filter((_, i) => i !== idx) : prev));
  };

  // ---------------- helpers ----------------
  const getField = (row, candidates = []) => {
    for (const key of candidates) {
      const v = row?.[key];
      if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  const sanitizeInlineInput = useCallback((value = '') => {
    const base = String(value ?? '').replace(/\r\n?|\n/g, ' ');
    return base
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }, []);

  const sanitizeZipCode = useCallback((value = '') => {
    return String(value ?? '')
      .replace(/[^0-9]/g, '')
      .slice(0, 11);
  }, []);

  const sanitizeVendorToken = useCallback((value = '') => {
    const cleaned = String(value ?? '')
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
      .replace(/[^A-Za-z0-9 &/.-]/g, '')
      .trim();
    if (!cleaned) return '';
    return cleaned.slice(0, 50);
  }, []);

  const addGlobalVendor = useCallback(
    (rawValue) => {
      const token = sanitizeVendorToken(rawValue);
      if (!token) return;
      setGlobalVendors((prev) => {
        if (prev.some((entry) => entry.toLowerCase() === token.toLowerCase())) return prev;
        return [...prev, token];
      });
    },
    [sanitizeVendorToken]
  );

  const removeGlobalVendor = useCallback((token) => {
    setGlobalVendors((prev) => prev.filter((entry) => entry !== token));
  }, []);

  const commitVendorInput = useCallback(() => {
    if (!globalVendorInput.trim()) return;
    addGlobalVendor(globalVendorInput);
    setGlobalVendorInput('');
  }, [globalVendorInput, addGlobalVendor]);

  const handleVendorInputKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' || event.key === ',') {
        event.preventDefault();
        commitVendorInput();
        return;
      }
      if (event.key === 'Backspace' && !globalVendorInput) {
        setGlobalVendors((prev) => prev.slice(0, -1));
      }
    },
    [commitVendorInput, globalVendorInput]
  );

  // Strictly sanitize search queries to remove special characters that break backend scripts.
  // Keeps only letters, numbers, spaces, hyphens, and dots.
  const sanitizeSearchQuery = useCallback((value = '') => {
    const base = String(value ?? '').replace(/\r\n?|\n/g, ' ');
    return base
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g, '')
      .replace(/[^A-Za-z0-9 \-.]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }, []);

  const buildQueryWithModifiers = useCallback(
    (value = '') => {
      const base = sanitizeSearchQuery(value);
      const zip = sanitizeZipCode(zipCode);
      const tokens = [];
      if (zip) {
        const chunk = `zipcode ${zip}`.trim();
        tokens.push(chunk);
      }
      (globalVendors || []).forEach((vendor) => {
        const cleaned = sanitizeVendorToken(vendor);
        if (cleaned) {
          tokens.push(cleaned);
        }
      });
      const combined = [base, ...tokens].filter(Boolean).join(' ');
      return sanitizeSearchQuery(combined);
    },
    [zipCode, globalVendors, sanitizeSearchQuery, sanitizeZipCode, sanitizeVendorToken]
  );

  const normalizeLaborHours = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(num) && num >= 0 ? num : 0;
  };

  function computeTotalsForRows(rows = []) {
    const sums = rows.reduce(
      (acc, entry) => {
        acc.material += Number(entry?.totalItemCost) || 0;
        acc.labor += Number(entry?.laborCost) || 0;
        return acc;
      },
      { material: 0, labor: 0 }
    );
    return { ...sums, total: sums.material + sums.labor };
  }


  const stripInternalFields = (rows = []) =>
    rows.map(({ laborKey, label, ...rest }) => rest);

  const sanitizeMultilineInput = useCallback((value = '') => {
    const base = String(value ?? '').replace(/\r\n?/g, '\n');
    return base.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g, '').trim();
  }, []);

  const cleanQuantityInput = (value = '') => {
    return String(value ?? '')
      .replace(/[^0-9.,/\s-]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const normalizeQuantityString = (value) => {
    if (value === undefined || value === null) return '';
    if (typeof value === 'number' && Number.isFinite(value)) return value > 0 ? String(value) : '';
    if (typeof value === 'string' && value.trim() === '') return '';

    const raw = String(value)
      .trim()
      .replace(/-/g, ' ');

    const mixedMatch = raw.match(/^(\d+)\s+(\d+)\s*\/\s*(\d+)$/);
    if (mixedMatch) {
      const whole = parseFloat(mixedMatch[1]);
      const numerator = parseFloat(mixedMatch[2]);
      const denominator = parseFloat(mixedMatch[3]);
      if (denominator) {
        const frac = numerator / denominator;
        const total = whole + frac;
        return Number.isFinite(total) && total > 0 ? String(total) : '';
      }
    }

    const fractionMatch = raw.match(/^(\d+)\s*\/\s*(\d+)$/);
    if (fractionMatch) {
      const numerator = parseFloat(fractionMatch[1]);
      const denominator = parseFloat(fractionMatch[2]);
      if (denominator) {
        const total = numerator / denominator;
        return Number.isFinite(total) && total > 0 ? String(total) : '';
      }
    }

    const cleaned = raw
      .replace(/,/g, '.')
      .replace(/[^0-9.]/g, '');
    if (!cleaned) return '';

    const parts = cleaned.split('.');
    const whole = parts.shift() || '';
    const decimal = parts.filter(Boolean).join('');
    const wholeSafe = whole || '0';
    const normalized = decimal ? `${wholeSafe}.${decimal}` : wholeSafe;
    return normalized.replace(/^0+([1-9])/, '$1');
  };

  const toPositiveQuantity = (value) => {
    const normalized = normalizeQuantityString(value);
    if (!normalized) return 1;
    const num = parseFloat(normalized);
    return Number.isFinite(num) && num > 0 ? num : 1;
  };

  const createEditableRow = (row = {}) => {
    const fallbackItem =
      row.item ||
      getField(row, ['item', 'Item', 'ITEM', 'Query', 'Search']) ||
      [row.vendor, row.model, row.description]
        .filter(Boolean)
        .join(' ');

    const item = sanitizeSearchQuery(fallbackItem);
    const clientPrompt = sanitizeInlineInput(row.clientPrompt || row.client_prompt || row.notes || '');
    const quantitySource =
      row.quantity ??
      row.qty ??
      row.count ??
      row.units ??
      getField(row, ['quantity', 'Quantity', 'QTY', 'Qty', 'Count', 'count']);
    const quantity = normalizeQuantityString(quantitySource) || '1';

    return { item, clientPrompt, quantity };
  };

  const uniqueItemCount = useMemo(() => {
    if (!Array.isArray(editableItems) || !editableItems.length) return 0;
    const seen = new Set();
    for (const entry of editableItems) {
      const query = sanitizeSearchQuery(entry?.item || '');
      if (!query) continue;
      const quantity = toPositiveQuantity(entry?.quantity);
      if (!(quantity > 0)) continue;
      seen.add(query.toLowerCase());
    }
    return seen.size;
  }, [editableItems, sanitizeSearchQuery]);


  useEffect(() => {
    let cancelled = false;

    if (!uniqueItemCount) {
      setCreditQuote((prev) => ({
        ...prev,
        totalCost: 0,
        shortfall: 0,
        breakdown: [],
        loading: false,
        error: '',
        available: creditBalance?.available ?? prev.available ?? 0,
      }));
      return;
    }

    setCreditQuote((prev) => ({
      ...prev,
      loading: true,
      error: '',
    }));

    (async () => {
      try {
        const data = await quoteCredits([{ feature: 'estimation_item', quantity: uniqueItemCount }]);
        if (cancelled) return;
        const totalCost = Number(data?.totalCost ?? uniqueItemCount);
        const available = Number(data?.available ?? creditBalance?.available ?? 0);
        const shortfall = Math.max(0, Number(data?.shortfall ?? totalCost - available));
        setCreditQuote({
          totalCost,
          available,
          shortfall,
          breakdown: Array.isArray(data?.breakdown) ? data.breakdown : [],
          loading: false,
          error: '',
        });
        if (Number.isFinite(available)) {
          setBalance((prev) => ({ ...prev, available }));
        }
      } catch (err) {
        if (cancelled) return;
        const available = Number(creditBalance?.available ?? 0);
        const shortfall = Math.max(0, uniqueItemCount - available);
        const message = err?.response?.data?.error || err?.message || 'Unable to fetch credit quote.';
        setCreditQuote({
          totalCost: uniqueItemCount,
          available,
          shortfall,
          breakdown: [],
          loading: false,
          error: message,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [uniqueItemCount, creditBalance?.available, setBalance]);

  // Reattach to pending estimate on mount (if any)
  useEffect(() => {
    const pending = loadPendingEstimate();
    if (!pending || !pending.requestId) {
      reattachPollRef.current = { stop: () => {} };
      return () => {};
    }

    let stopped = false;
    let pollTimer = null;
    const stopPolling = () => {
      stopped = true;
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = null;
      }
    };
    reattachPollRef.current = { stop: stopPolling };

    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (stopped) return;
        const token = session?.access_token || '';
        const base = api.defaults?.baseURL ? api.defaults.baseURL.replace(/\/+$/, '') : '/api';
        const requestId = pending.requestId;
        creditRequestRef.current = requestId;
        setActiveRequestId(requestId);

        // Initialize a minimal pricingProgress so UI shows something
        const seedEntries = (pending.payload || []).map((p) => ({
          query: p.query,
          status: 'pending',
          percent: 0,
          vendorsProcessed: 0,
        }));
        const totalItems = pending.payload?.length || 0;
        const summary = summarizeProgress(seedEntries, totalItems);
        setPricingProgress(() => ({
          total: totalItems,
          completed: summary.completed,
          overallPercent: summary.percent,
          cancelled: false,
          entries: seedEntries,
        }));
        optionsCacheRef.current = new Map();
        setOptionsRevision((prev) => prev + 1);

        // Poll until done
        const poll = async () => {
          if (stopped) return;
          try {
            const statusRes = await fetch(`${base}/prices/status?requestId=${encodeURIComponent(requestId)}`, {
              headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            });
            if (!statusRes.ok) throw new Error('Failed to fetch pricing status');
            const manifest = await statusRes.json();
            if (stopped) return;
            if (!manifest || !Array.isArray(manifest.entries)) return;
            // Update UI entries from manifest
            setPricingProgress((prev) => {
              if (!prev) return prev;
              const entries = [...prev.entries];
              for (const e of manifest.entries) {
                const idx = Number.isFinite(e.index) ? e.index : null;
                if (idx == null || idx < 0 || idx >= entries.length) continue;
                const next = { ...(entries[idx] || {}) };
                next.status = e.status || next.status;
                next.percent = Number.isFinite(e.percent) ? e.percent : next.percent || 0;
                if (e.refunded) {
                  next.refunded = true;
                  next.refundReason = e.refundReason || null;
                }
                entries[idx] = next;
                // Upsert any manifest-provided results
                if (e.results && e.results.length) {
                  const r = e.results[0];
                  const querySource = resolveQuerySource(e.query, idx) || r.query || e.query || '';
                  const enriched = { ...r, query: querySource };
                  if (enriched.query) {
                    upsertResult(enriched);
                    updateOptionsCache(enriched.query, e.results);
                  }
                }
              }
              const summaryInner = summarizeProgress(entries, prev.total);
              return { ...prev, entries, completed: summaryInner.completed, overallPercent: summaryInner.percent };
            });

            if (manifest.done) {
              clearPendingEstimate();
              stopPolling();
              return;
            }
          } catch (err) {
            // ignore transient errors, keep polling
          }
          if (!stopped) {
            pollTimer = setTimeout(poll, 2000);
          }
        };
        poll();
      } catch (err) {
        /* ignore */
      }
    })();

    return () => {
      stopPolling();
    };
  }, []);

  // detect binary/gibberish (non-printable outside \t \n \r and ASCII range)
  const looksBinary = (s) => /[^\x09\x0A\x0D\x20-\x7E]/.test(s || '');

  const isJunkLine = (s) => {
    const t = (s || '').toLowerCase().trim();
    if (!t) return true;
    if (looksBinary(t)) return true;
    // page/cover/header/admin lines
    if (/^(job|project)\s*(number|name|location)\b/i.test(t)) return true;
    if (/(architect|client|issue date|sheet|page|schedule)\b/i.test(t)) return true;
    if (/^(notes?|remarks?)\b/i.test(t)) return true;
    // separators or very short
    if (/^[-_.,\s]+$/.test(t)) return true;
    if (t.replace(/[^a-z0-9]/g, '').length < 3) return true;
    return false;
  };

  // naive CSV splitter that copes with quoted commas
  const splitCsvLine = (line) => {
    const out = [];
    let cur = '';
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
        else { inQ = !inQ; }
      } else if (ch === ',' && !inQ) {
        out.push(cur); cur = '';
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out.map(s => s.trim());
  };

  // stricter model guess: must contain both letters & digits OR include a dash/slash with digits
  const guessModelFrom = (arrOrStr) => {
    const tokens = Array.isArray(arrOrStr) ? arrOrStr : splitCsvLine(String(arrOrStr));
    const PAT = /(?=.*[A-Z])(?=.*\d)[A-Z0-9\-\/]{3,}|[A-Z0-9]{2,}[-/][A-Z0-9\-\/]{1,}/i;
    for (const t of tokens) {
      const m = (t || '').match(PAT);
      if (m && !/qty|notes|manufacturer|description|job|project/i.test(t)) return m[0];
    }
    return '';
  };

  const guessVendorFrom = (arrOrStr) => {
    const ts = Array.isArray(arrOrStr) ? arrOrStr : splitCsvLine(String(arrOrStr));
    const vendors = ['bobrick','asi','schlage','wingits','hadrian','metpar','bradley','toto','kohler','moen','grainger','ferguson','home depot','lowes','whitecap'];
    for (const t of ts) {
      const low = (t || '').toLowerCase();
      if (vendors.some(v => low.includes(v))) return t;
    }
    return '';
  };

  const guessDescriptionFrom = (arrOrStr, model, vendor) => {
    const ts = Array.isArray(arrOrStr) ? arrOrStr : splitCsvLine(String(arrOrStr));
    const bad = new Set([model?.toLowerCase(), vendor?.toLowerCase()]);
    const maybe = ts
      .filter(Boolean)
      .filter(t => !/qty|model|manufacturer|notes|job|project/i.test(t))
      .filter(t => !bad.has(t.toLowerCase()))
      .filter(t => t.length > 2 && !looksBinary(t));
    maybe.sort((a,b)=>b.length-a.length);
    const pick = maybe.find(t => /[a-z]/i.test(t)) || maybe[0] || '';
    return pick;
  };

  const buildQuery = ({ item, vendor, model, description }) => {
    const candidate = (item || '').trim();
    if (candidate) return candidate;
    return [vendor, model, description].map(s => (s || '').trim()).filter(Boolean).join(' ');
  };

  // ---------------- CSV download ----------------
  const downloadCSV = (breakdown, label = 'estimate') => {
    const rows = [];
    rows.push(['Item', 'Qty', 'Unit Cost', 'Material Total', 'Source', 'Link']);
    (Array.isArray(breakdown) ? breakdown : []).forEach((r) => {
      rows.push([
        r.label || r.item || '',
        r.quantity,
        Number(r.unitCost || 0).toFixed(2),
        Number(r.totalItemCost || 0).toFixed(2),
        r.source || '',
        r.product_link || '',
      ]);
    });

    if (hasLaborFeature && laborEntriesDetailed.length > 0) {
      rows.push([]);
      rows.push(['Labor Role', 'Hours', 'Rate', 'Cost', 'Notes']);
      laborEntriesDetailed.forEach((entry) => {
        rows.push([
          entry.name || '',
          entry.hours.toFixed(2),
          entry.rate.toFixed(2),
          entry.cost.toFixed(2),
          entry.notes || '',
        ]);
      });
      rows.push(['Total', laborTotals.hours.toFixed(2), '', laborTotals.cost.toFixed(2), '']);
    }

    const csvContent = rows
      .map((row) => row.map((value) => `"${value ?? ''}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${(label || 'estimate').replace(/\s+/g, '_')}_estimate.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ---------------- server calls ----------------
  const getExtractedHeaders = async () => {
    if (!partsList?.length && !uploadedFile) {
      setError('No parts to extract. Upload a file or use manual entry instead.');
      return;
    }

    setLoading(true);
    setError('');
    // removed CSV analysis box
    setEditableItems(null);
    clearResultState();

    try {
      const formData = new FormData();

      if (uploadedFile instanceof Blob) {
        // ✅ Best path: send the real file (CSV or XLSX) to the server.
        // Rely on server to convert XLSX → CSV and extract.
        const name = uploadedFile.name || (tableLabel ? `${tableLabel}.xlsx` : 'parts.xlsx');
        formData.append('csv', uploadedFile, name);
      } else {
        // Fallback: build minimal CSV *only if* it’s clean ASCII text
        const lines = (partsList || [])
          .map((row) => getField(row, ['item','Item','Query','Search']) || '')
          .filter(s => s && !isJunkLine(s) && !looksBinary(s));

        if (!lines.length) {
          throw new Error('Could not build a clean CSV from provided text. Please upload the original CSV/XLSX.');
        }

        const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
        formData.append('csv', blob, tableLabel || 'parts.csv');
      }

      const resp = await api.post('/estimates/extract-headers', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const rows = Array.isArray(resp.data?.rows) ? resp.data.rows : [];
      const shaped = rows.map((r) =>
        createEditableRow({
          ...r,
          item: r.item || [r.vendor, r.model, r.description].filter(Boolean).join(' '),
        })
      );

      // no CSV analysis display
      setEditableItems(shaped.length ? shaped : [createEditableRow({})]);
      setHasExtracted(true);
    } catch (err) {
      console.error('[CostEstimator] extractHeaders error', err);
      setError(err?.message || 'Failed to extract items.');
      // no CSV analysis display
    } finally {
      setLoading(false);
    }
  };

  const confirmAndFetchEstimate = async () => {

  if (Date.now() < nextAllowedConfirm.current) {
    toast.info('Please wait a moment...');
    return;
  }
  if (creditQuote.shortfall > 0) {
    toast.error(`Not enough credits. You need ${creditQuote.shortfall} more.`);
    return;
  }
  if (creditQuote.loading) {
    toast.info('Calculating credit requirement, please wait...');
    return;
  }

  const globalPromptClean = sanitizeMultilineInput(globalPrompt);

  try {
    const rows = (editableItems || [])
      .map((entry) => {
        const rawValue = entry?.item || '';
        const baseQuery = sanitizeSearchQuery(rawValue);
        if (!baseQuery) return null;
        const query = buildQueryWithModifiers(baseQuery);
        if (!query) return null;
        const quantity = toPositiveQuantity(entry?.quantity);
        const clientPrompt = sanitizeInlineInput(entry?.clientPrompt || '');
        return {
          ...entry,
          item: query,
          label: baseQuery,
          clientPrompt,
          quantity,
          _query: query,
          preferred_vendors: globalVendors,
        };
      })
      .filter(Boolean);

    if (!rows.length) {
      toast.error('No items to price.');
      setPricingProgress(null);
      return;
    }

    const seen = new Set();
    const payload = [];
    for (const row of rows) {
      const key = row._query.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      const itemPayload = { query: row._query, label: row.label || row.item };
      if (row.clientPrompt) itemPayload.client_prompt = row.clientPrompt;
      payload.push(itemPayload);
    }

    if (!payload.length) {
      toast.error('No items to price.');
      setPricingProgress(null);
      return;
    }

    nextAllowedConfirm.current = Date.now() + COOLDOWN_MS;
    creditRequestRef.current = generateCreditRequestId();
    setActiveRequestId(creditRequestRef.current);

    controllersRef.current = [];
    setEditState(null);
    setInlineLoadingIndex(null);
    setConfirming(true);
    const seededEntries = payload.map((itm) => ({
      query: itm.query,
      label: itm.label,
      status: 'pending',
      percent: 0,
      durationMs: null,
      error: null,
      results: null,
      vendorsProcessed: 0,
    }));
    const summary = summarizeProgress(seededEntries, payload.length);
    setPricingProgress({
      total: payload.length,
      completed: summary.completed,
      overallPercent: summary.percent,
      cancelled: false,
      entries: seededEntries,
    });
    setExpandedRows({});
    setSelectedOptions({});
    optionsCacheRef.current = new Map();
    setOptionsRevision((prev) => prev + 1);
    payloadRef.current = payload;
    pricingRowsRef.current = rows;
    aggregatedRef.current = new Map();
    clearResultState();
    recomputeBreakdown();

    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token || '';
    const base = api.defaults?.baseURL
      ? api.defaults.baseURL.replace(/\/+$/, '')
      : '/api';

    const requestBody = {
      items: payload,
      vendors: selectedVendors,
      creditRequestId: creditRequestRef.current,
      searchMode: vendorSearchMode,
    };
    if (selectedVendorAccountIds.length) {
      requestBody.vendorAccountIds = selectedVendorAccountIds;
    }
    if (globalPromptClean) {
      requestBody.globalPrompt = globalPromptClean;
    }
    if (globalVendors.length) {
      requestBody.preferredVendors = globalVendors;
    }

    try {
      savePendingEstimate({
        requestId: creditRequestRef.current,
        payload: payloadRef.current,
        ts: Date.now(),
      });
    } catch (err) {
      // ignore storage errors
    }

    const finishedIndexes = new Set();

    const handleStreamEvent = (eventType, raw = {}) => {
      const evt = raw && typeof raw === 'object' ? raw : {};

      if (eventType === 'credits') {
        if (typeof evt.requestId === 'string' && evt.requestId.trim()) {
          const rid = evt.requestId.trim();
          creditRequestRef.current = rid;
          setActiveRequestId(rid);
          try {
            savePendingEstimate({
              requestId: rid,
              payload: payloadRef.current,
              ts: Date.now(),
            });
          } catch (storageErr) {
            /* ignore */
          }
        }
        if (typeof evt.available === 'number') {
          setCreditQuote((prev) => ({
            ...prev,
            available: evt.available,
            loading: false,
            error: '',
          }));
          setBalance((prev) => ({ ...prev, available: evt.available }));
        }
        if (typeof evt.spent === 'number') {
          setCreditQuote((prev) => ({
            ...prev,
            totalCost: evt.spent,
            shortfall: 0,
            error: '',
          }));
        }
        return;
      }

      const index = Number.isFinite(evt.index) ? evt.index : null;
      const updateEntry = (updater) => {
        if (index == null) return;
        setPricingProgress((prev) => {
          if (!prev || index < 0 || index >= prev.entries.length) return prev;
          const entries = [...prev.entries];
          const current = entries[index] || {};
          const next = updater({ ...current });
          entries[index] = next;
          const summary = summarizeProgress(entries, prev.total);
          return { ...prev, entries, completed: summary.completed, overallPercent: summary.percent };
        });
      };

      const querySource = resolveQuerySource(evt.query, index);

      if (eventType === 'item-start') {
        updateEntry((entry) => ({
          ...entry,
          status: 'running',
          percent: Math.max(entry.percent || 0, 25),
          error: null,
        }));
        return;
      }

      if (eventType === 'item-progress') {
        const candidates = Array.isArray(evt.results) ? evt.results : [];
        const bestCandidate = evt.best || (candidates.length ? candidates[0] : null);
        updateEntry((entry) => ({
          ...entry,
          percent: Number.isFinite(evt.percent) ? Math.max(entry.percent || 0, evt.percent) : entry.percent,
          vendorsProcessed: Number.isFinite(evt.vendorsProcessed) ? evt.vendorsProcessed : entry.vendorsProcessed,
          results: candidates.length ? candidates : entry.results,
        }));
        if (bestCandidate && bestCandidate.query) {
          upsertResult(bestCandidate);
        }
        if (querySource) {
          updateOptionsCache(querySource, candidates.length ? candidates : bestCandidate ? [bestCandidate] : []);
        }
        return;
      }

      if (eventType === 'item-complete') {
        const results = Array.isArray(evt.results) ? evt.results : (evt.best ? [evt.best] : []);
        updateEntry((entry) => ({
          ...entry,
          status: 'done',
          percent: 100,
          durationMs: evt.durationMs ?? entry.durationMs,
          error: null,
          vendorsProcessed: Number.isFinite(evt.vendorsProcessed) ? evt.vendorsProcessed : entry.vendorsProcessed,
          results,
        }));
        const primary = results[0] || evt.best || null;
        if (primary && primary.query) {
          upsertResult(primary);
        }
        if (querySource) {
          updateOptionsCache(querySource, results);
        }
        return;
      }

      if (eventType === 'item-error') {
        const results = Array.isArray(evt.results) ? evt.results : [];
        const best = evt.best || (results.length ? results[0] : null);
        updateEntry((entry) => ({
          ...entry,
          status: 'error',
          percent: 100,
          durationMs: evt.durationMs ?? entry.durationMs,
          error: evt.error || 'Unable to fetch price',
          vendorsProcessed: Number.isFinite(evt.vendorsProcessed) ? evt.vendorsProcessed : entry.vendorsProcessed,
          results: results.length ? results : entry.results,
        }));
        if (best && best.query) {
          upsertResult(best);
        }
        if (querySource) {
          const payload = results.length
            ? results
            : best
              ? [best]
              : [];
          if (payload.length) {
            updateOptionsCache(querySource, payload);
          }
        }
        return;
      }

      if (eventType === 'fatal') {
        toast.error(evt.error || 'Pricing run failed.');
        setPricingProgress((prev) => {
          if (!prev) return prev;
          const summary = summarizeProgress(prev.entries, prev.total);
          return { ...prev, cancelled: true, completed: summary.completed, overallPercent: summary.percent };
        });
      }
    };

    const streamEstimate = () => new Promise((resolve, reject) => {
      if (typeof EventSource === 'undefined') {
        reject(new Error('EventSource is not supported in this browser.'));
        return;
      }

      const payloadParam = encodeURIComponent(JSON.stringify(requestBody));
      const tokenQuery = token ? `&token=${encodeURIComponent(token)}` : '';
      const url = `${base}/prices?payload=${payloadParam}${tokenQuery}`;

      const source = new EventSource(url);
      let cancelled = false;
      let closed = false;

      const connection = {
        close: () => {
          cancelled = true;
          source.close();
        },
      };
      controllersRef.current.push(connection);

      const cleanup = () => {
        if (closed) return;
        closed = true;
        source.close();
        controllersRef.current = controllersRef.current.filter((ctrl) => ctrl !== connection);
      };

      const parseEvent = (event) => {
        if (!event?.data) return {};
        try {
          return JSON.parse(event.data);
        } catch (err) {
          console.error('[CostEstimator] SSE parse error', event.type, err, event.data);
          return {};
        }
      };

      const markFinished = (idx) => {
        if (!Number.isInteger(idx)) return false;
        if (finishedIndexes.has(idx)) return false;
        finishedIndexes.add(idx);
        return finishedIndexes.size >= payload.length;
      };

      source.addEventListener('credits', (event) => {
        handleStreamEvent('credits', parseEvent(event));
      });

      source.addEventListener('item-start', (event) => {
        handleStreamEvent('item-start', parseEvent(event));
      });

      source.addEventListener('item-progress', (event) => {
        handleStreamEvent('item-progress', parseEvent(event));
      });

      source.addEventListener('item-complete', (event) => {
        const data = parseEvent(event);
        handleStreamEvent('item-complete', data);
        if (markFinished(Number(data.index))) {
          cleanup();
          resolve();
        }
      });

      source.addEventListener('item-error', (event) => {
        const data = parseEvent(event);
        handleStreamEvent('item-error', data);
        if (markFinished(Number(data.index))) {
          cleanup();
          resolve();
        }
      });

      source.addEventListener('fatal', (event) => {
        const data = parseEvent(event);
        handleStreamEvent('fatal', data);
        cleanup();
        const err = new Error(data?.error || 'Pricing run failed.');
        if (data?.code) err.code = data.code;
        reject(err);
      });

      source.addEventListener('error', (event) => {
        if (source.readyState === EventSource.CLOSED) {
          cleanup();
          if (cancelled) {
            const abortErr = new Error('Pricing stream cancelled');
            abortErr.name = 'AbortError';
            reject(abortErr);
          } else if (finishedIndexes.size >= payload.length) {
            resolve();
          } else {
            const err = new Error('Connection closed unexpectedly.');
            err.code = 'SSE_CLOSED';
            reject(err);
          }
        } else {
          console.warn('[CostEstimator] SSE transient error', event);
        }
      });
    });

    await streamEstimate();
    setEditableItems(null);
    clearPendingEstimate();
  } catch (err) {
    if (err?.code === 'INSUFFICIENT_CREDITS') {
      const shortfall = Math.max(0, Number(err?.shortfall ?? 0));
      setCreditQuote((prev) => ({
        ...prev,
        shortfall,
        loading: false,
        error: err?.message || 'Insufficient credits',
      }));
      if (typeof err?.available === 'number') {
        setBalance((prev) => ({ ...prev, available: err.available }));
      }
      toast.error(err?.message || 'Not enough credits to run this estimate.');
      creditRequestRef.current = '';
      setActiveRequestId('');
    } else if (err?.name === 'AbortError') {
      toast.info('Pricing run cancelled');
    } else {
      console.error('[CostEstimator] pricing error', err);
      toast.error(err?.message || 'Failed to fetch pricing');
      creditRequestRef.current = '';
      setActiveRequestId('');
    }
  } finally {
    controllersRef.current = [];
    setConfirming(false);
    refreshBalance().catch(() => {});
  }
};

  const handleCancelPricing = async () => {
    controllersRef.current.forEach((controller) => {
      try {
        if (controller && typeof controller.close === 'function') {
          controller.close();
        } else if (controller && typeof controller.abort === 'function') {
          controller.abort();
        }
      } catch (err) {
        console.warn('[CostEstimator] cancel error', err);
      }
    });
    controllersRef.current = [];
    if (reattachPollRef.current?.stop) {
      reattachPollRef.current.stop();
    }
    setExpandedRows({});
    setSelectedOptions({});
    setPricingProgress((prev) => {
      if (!prev) return prev;
      const entries = prev.entries.map((entry) => {
        if (entry.status === 'done' || entry.status === 'error') return entry;
        return {
          ...entry,
          status: 'cancelled',
          percent: Math.max(entry.percent, 100),
          error: entry.error || 'Cancelled by user'
        };
      });
      const summary = summarizeProgress(entries, prev.total);
      return { ...prev, entries, completed: summary.completed, overallPercent: summary.percent, cancelled: true };
    });
    setConfirming(false);
    try {
      clearPendingEstimate();
    } catch (storageErr) {
      /* ignore */
    }
    setEditState(null);
    setInlineLoadingIndex(null);

    const requestId = (creditRequestRef.current || activeRequestId || '').trim();
    if (!requestId) {
      creditRequestRef.current = '';
      setActiveRequestId('');
      toast.info('Cancelled local pricing session.');
      if (reattachPollRef.current?.stop) {
        reattachPollRef.current.stop();
      }
      return;
    }

    setCancelling(true);
    let cleared = false;
    try {
      await api.post('/prices/cancel', { requestId });
      toast.success('Cancelled pricing request.');
      cleared = true;
    } catch (err) {
      console.error('[CostEstimator] cancel request error', err);
      const message = err?.response?.data?.error || err?.message || 'Failed to cancel pricing request.';
      toast.error(message);
    } finally {
      setCancelling(false);
      if (cleared) {
        creditRequestRef.current = '';
        setActiveRequestId('');
        if (reattachPollRef.current?.stop) {
          reattachPollRef.current.stop();
        }
        try {
          clearPendingEstimate();
        } catch (storageErr) {
          /* ignore */
        }
      }
      refreshBalance().catch(() => {});
    }
  };

  const beginInlineEdit = (rowIndex, itemValue, quantityValue) => {
    const baseQuantity = normalizeQuantityString(quantityValue) || normalizeQuantityString('1') || '1';
    setEditState({
      rowIndex,
      query: itemValue || '',
      quantity: baseQuantity,
    });
    setInlineLoadingIndex(null);
  };

  const handleInlineEditCancel = () => {
    if (inlineLoadingIndex !== null) return;
    setEditState(null);
  };

  const handleInlineEditSubmit = async () => {
    if (!editState) return;
    const { rowIndex } = editState;
    if (rowIndex == null || rowIndex < 0) return;

    const requestId = (activeRequestId || '').trim();
    if (!requestId) {
      toast.error('Unable to locate the original pricing request. Please run pricing again.');
      return;
    }

    const rawQuery = editState.query || '';
    const baseQuery = sanitizeSearchQuery(rawQuery);
    if (!baseQuery) {
      toast.error('Query must contain letters or numbers.');
      return;
    }
    const queryWithModifiersValue = buildQueryWithModifiers(baseQuery);

    const quantityInput = editState.quantity || '1';
    const normalizedQuantityString = normalizeQuantityString(quantityInput) || '1';
    const normalizedQuantity = toPositiveQuantity(normalizedQuantityString);

    setInlineLoadingIndex(rowIndex);
    try {
      const response = await api.post('/prices/rerun', {
        requestId,
        itemIndex: rowIndex,
        query: queryWithModifiersValue,
        quantity: normalizedQuantity,
        preferredVendors: globalVendors,
      });
      const data = response?.data || {};
      const candidates = Array.isArray(data.results) ? data.results : [];
      const bestCandidate = data.best && typeof data.best === 'object' ? data.best : candidates[0] || null;
      if (!bestCandidate) {
        throw new Error('No pricing results were returned for this query.');
      }

      const oldRow = pricingRowsRef.current[rowIndex] || {};
      const oldQueryKey = normalizeQueryKey(oldRow._query || oldRow.item || '');
      const newQueryKey = normalizeQueryKey(queryWithModifiersValue);

      const normalizedQuantityDisplay = normalizeQuantityString(String(normalizedQuantity)) || String(normalizedQuantity);

      pricingRowsRef.current[rowIndex] = {
        ...oldRow,
        item: queryWithModifiersValue,
        label: baseQuery,
        _query: queryWithModifiersValue,
        quantity: normalizedQuantityDisplay,
      };

      setEditableItems((prev) => {
        if (!Array.isArray(prev) || prev.length <= rowIndex) return prev;
        const next = [...prev];
        const existing = next[rowIndex] || {};
        next[rowIndex] = {
          ...existing,
          item: baseQuery,
          quantity: normalizedQuantityDisplay,
        };
        return next;
      });

      if (Array.isArray(payloadRef.current)) {
        const nextPayload = [...payloadRef.current];
        if (nextPayload[rowIndex]) {
          nextPayload[rowIndex] = {
            ...nextPayload[rowIndex],
            query: queryWithModifiersValue,
            label: baseQuery,
            preferred_vendors: globalVendors,
          };
          payloadRef.current = nextPayload;
        }
      }

      aggregatedRef.current.delete(oldQueryKey);
      if (oldQueryKey && oldQueryKey !== newQueryKey) {
        optionsCacheRef.current.delete(oldQueryKey);
      }
      aggregatedRef.current.set(newQueryKey, {
        ...(bestCandidate || {}),
        query: queryWithModifiersValue,
      });

      setSelectedOptions((prev) => {
        const next = { ...prev };
        if (oldQueryKey && oldQueryKey !== newQueryKey) delete next[oldQueryKey];
        next[newQueryKey] = 0;
        return next;
      });

      setExpandedRows((prev) => {
        const next = { ...prev };
        if (oldQueryKey && oldQueryKey !== newQueryKey) delete next[oldQueryKey];
        return next;
      });

      updateOptionsCache(queryWithModifiersValue, candidates.length ? candidates : bestCandidate ? [bestCandidate] : []);
      recomputeBreakdown();

      setPricingProgress((prev) => {
        if (!prev) return prev;
        const entries = [...prev.entries];
        if (entries[rowIndex]) {
          entries[rowIndex] = {
            ...entries[rowIndex],
            status: 'done',
            percent: 100,
            query: queryWithModifiersValue,
            label: baseQuery,
            job: data.jobId || entries[rowIndex].job,
            results: candidates,
            vendorsProcessed: Number.isFinite(data.vendorsProcessed)
              ? data.vendorsProcessed
              : entries[rowIndex].vendorsProcessed || candidates.length,
            error: null,
            refunded: false,
            refundReason: null,
            refundedAt: null,
            quantity: normalizedQuantity,
          };
        }
        const summary = summarizeProgress(entries, prev.total);
        return { ...prev, entries, completed: summary.completed, overallPercent: summary.percent };
      });

      try {
        savePendingEstimate({
          requestId,
          payload: payloadRef.current,
          ts: Date.now(),
        });
      } catch (storageErr) {
        /* ignore */
      }

      if (Number.isFinite(data.availableCredits) || Number.isFinite(data.spentCredits)) {
        setCreditQuote((prev) => {
          const nextAvailable = Number.isFinite(data.availableCredits)
            ? data.availableCredits
            : prev.available;
          const nextTotal = (prev.totalCost || 0) + (Number.isFinite(data.spentCredits) ? data.spentCredits : 0);
          const nextShortfall = Number.isFinite(nextAvailable)
            ? Math.max(0, nextTotal - nextAvailable)
            : prev.shortfall;
          return {
            ...prev,
            available: nextAvailable,
            totalCost: nextTotal,
            shortfall: nextShortfall,
          };
        });
        if (Number.isFinite(data.availableCredits)) {
          setBalance((prev) => ({ ...prev, available: data.availableCredits }));
        }
      }

      setEditState(null);
      toast.success('Item updated.');
    } catch (err) {
      console.error('[CostEstimator] inline rerun error', err);
      const message = err?.response?.data?.error || err?.message || 'Failed to refresh this item.';
      toast.error(message);
    } finally {
      setInlineLoadingIndex(null);
      refreshBalance().catch(() => {});
    }
  };

  const handleSave = async () => {
    if (!activeProject?._id) return toast.error('No project selected.');
    if (!result) return toast.error('Nothing to save yet.');

    setSaving(true);
    try {
      const laborPayload = laborEntries.map((entry) => ({
        id: entry.id,
        roleId: entry.roleId,
        hours: Number(entry.hours) || 0,
        notes: entry.notes || '',
        rateOverride: entry.rateOverride ?? null,
      }));
      const material = result.materialTotal || 0;
      const laborCost = laborTotals.cost;
      await api.post(`/projects/${activeProject._id}/tables`, {
        label: tableLabel || new Date().toISOString(),
        breakdown: stripInternalFields(result.breakdown),
        totalCost: material + laborCost,
        materialTotal: material,
        laborTotal: laborCost,
        laborEntries: laborPayload,
      });
      toast.success('Saved estimate to project!');
    } catch (err) {
      console.error('[CostEstimator] save error', err);
      toast.error('Failed to save estimate.');
    } finally {
      setSaving(false);
    }
  };

  const creditsNeeded = creditQuote.totalCost || uniqueItemCount;
  const creditBalanceAvailable = creditBalance?.available ?? 0;
  const disableConfirm = confirming || cancelling || creditQuote.loading || creditQuote.shortfall > 0;
  const isPricingActive =
    Boolean(
      pricingProgress &&
        !pricingProgress.cancelled &&
        (pricingProgress.completed || 0) < (pricingProgress.total || 0)
    );
  const canCancelPricing = Boolean(isPricingActive || (controllersRef.current?.length || 0));
  const disableCancel = !canCancelPricing || cancelling;
  const tableColumnCount = 7;
  const materialTotal = result?.materialTotal || 0;
  const laborTotal = hasLaborFeature ? laborTotals.cost : 0;
  const grandTotal = materialTotal + laborTotal;

  // ---------------- render ----------------
  return (
    <div className="mt-8 space-y-8 text-zinc-200">
      {/* Section: Get Estimate */}
      <section className={`${card} p-6`}>
        <h2 className="mb-4 text-xl font-semibold text-amber-300">2. Get Estimate</h2>
        {vendorSearchMode !== 'database' && (
        <>
        <div className="mb-4 w-full max-w-3xl">
          <label htmlFor="global-prompt" className="mb-2 block text-sm font-medium text-zinc-300">
            Global Prompt (optional)
          </label>
          <textarea
            id="global-prompt"
            value={globalPrompt}
            onChange={(e) => setGlobalPrompt(e.target.value)}
            placeholder="Describe the overall goal for these items"
            className={`${input} min-h-[96px] resize-y`}
          />
          <p className="mt-2 text-sm text-zinc-400">
            Shared with every item to steer search results toward the overall requirements.
          </p>
        </div>

        <div className="mb-4 w-full max-w-xs">
          <label htmlFor="zip-code" className="mb-2 block text-sm font-medium text-zinc-300">
            Zip Code (optional)
          </label>
          <input
            id="zip-code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={zipCode}
            onChange={(e) => setZipCode(sanitizeZipCode(e.target.value))}
            placeholder="e.g., 90210"
            className={input}
          />
        </div>

        <div className="mb-6 w-full max-w-3xl">
          <label htmlFor="global-vendors" className="mb-2 block text-sm font-medium text-zinc-300">
            Preferred Vendors (optional)
          </label>
          <div
            className="flex min-h-[48px] flex-wrap gap-2 rounded-lg border border-white/10 bg-zinc-900/60 px-3 py-2"
            onClick={() => document.getElementById('global-vendors-input')?.focus()}
          >
            {globalVendors.map((vendor) => (
              <span
                key={vendor}
                className="inline-flex items-center gap-2 rounded-full bg-amber-500/20 px-3 py-1 text-sm text-amber-100"
              >
                {vendor}
                <button
                  type="button"
                  onClick={() => removeGlobalVendor(vendor)}
                  className="text-xs text-amber-200 hover:text-amber-50"
                  aria-label={`Remove ${vendor}`}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              id="global-vendors-input"
              value={globalVendorInput}
              onChange={(e) => setGlobalVendorInput(e.target.value)}
              onKeyDown={handleVendorInputKeyDown}
              onBlur={commitVendorInput}
              placeholder="Type a vendor name and press Enter"
              className="flex-1 bg-transparent text-sm text-zinc-100 placeholder:text-zinc-400 focus:outline-none"
            />
          </div>
        </div>

          </>
        )}

        <div className="mb-6 w-full max-w-3xl">
          <span className="mb-2 block text-sm font-medium text-zinc-300">Search Mode</span>
          <div className="flex flex-wrap gap-3 text-sm">
            {[
              { value: 'live', label: 'Web' },
              { value: 'database', label: 'Vendor DB' },
              { value: 'both', label: 'Both' },
            ].map((opt) => (
              <label
                key={opt.value}
                className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 ${
                  vendorSearchMode === opt.value
                    ? 'border-amber-400/60 bg-amber-400/10 text-amber-100'
                    : 'border-white/10 bg-zinc-900/60 text-zinc-200'
                }`}
              >
                <input
                  type="radio"
                  name="vendor-search-mode"
                  value={opt.value}
                  checked={vendorSearchMode === opt.value}
                  onChange={() => setVendorSearchMode(opt.value)}
                  className="text-amber-400 focus:ring-amber-400"
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        {vendorSearchMode !== 'live' && (
          <div className="mb-6 w-full max-w-3xl">
            <div className="flex items-center justify-between text-sm font-medium text-zinc-300">
              <span>Vendor DBs</span>
              <button
                type="button"
                className="text-xs text-amber-300 hover:text-amber-200"
                onClick={() => setSelectedVendorAccountIds([])}
              >
                {selectedVendorAccountIds.length ? 'Use all' : 'All selected'}
              </button>
            </div>
            <div className="relative mt-2 inline-block">
              <button
                type="button"
                ref={vendorDbBtnRef}
                onClick={() => setVendorDbMenuOpen((v) => !v)}
                className="glass-ghost flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white hover:border-amber-400/50"
              >
                {vendorAccountsLoading
                  ? 'Loading vendor DBs…'
                  : selectedVendorAccountIds.length
                    ? `${selectedVendorAccountIds.length} selected`
                    : 'All vendor DBs'}
                <span className="text-xs text-zinc-400">▼</span>
              </button>
              {vendorDbMenuOpen && (
                <div
                  ref={vendorDbMenuRef}
                  className="absolute z-20 mt-2 w-80 rounded-lg border border-white/10 bg-zinc-900/95 shadow-2xl"
                >
                  <div className="max-h-80 overflow-y-auto p-2 text-sm text-zinc-200">
                    {vendorAccounts.length ? (
                      vendorAccounts.map((acct) => {
                        const checked = selectedVendorAccountIds.includes(acct.id);
                        return (
                          <label
                            key={acct.id}
                            className="flex cursor-pointer items-start gap-3 rounded-md px-2 py-2 hover:bg-white/5"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                setSelectedVendorAccountIds((prev) => {
                                  if (e.target.checked) return [...prev, acct.id];
                                  return prev.filter((id) => id !== acct.id);
                                });
                              }}
                              className="mt-1 h-4 w-4 cursor-pointer rounded border border-zinc-500 bg-zinc-900 text-amber-400 accent-amber-400 outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:ring-0 focus:ring-offset-0 shadow-none focus:shadow-none"
                              style={{ boxShadow: 'none' }}
                            />
                            <div className="flex flex-col">
                              <span className="font-semibold text-white">{acct.name}</span>
                              {acct.website && (
                                <span className="text-xs text-amber-200">{acct.website}</span>
                              )}
                            </div>
                          </label>
                        );
                      })
                    ) : (
                      <p className="px-2 py-3 text-xs text-zinc-400">
                        No vendor DBs yet. Create one under Vendors.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          {!manualMode && !hasExtracted && (
            <button onClick={getExtractedHeaders} disabled={loading} className={btnPrimary}>
              {loading ? 'Extracting...' : 'Extract Items'}
            </button>
          )}

          {/* Vendor Filter (improved popover) */}
          <div className="relative inline-block">
            {showVendorFilter && (
              <>
                <button
                  type="button"
                  ref={vendorBtnRef}
                  onClick={() => {
                    setVendorMenuOpen(v => !v);
                  }}
                  className={btnOutline}
                  aria-haspopup="true"
                  aria-expanded={vendorMenuOpen}
                >
                  {selectedVendors.length ? `Vendors (${selectedVendors.length})` : 'Filter by Vendors'}
                </button>

                {vendorMenuOpen && createPortal((
                  <div
                    ref={vendorMenuRef}
                    className="fixed z-[10000] w-[22rem] rounded-xl border border-white/10 bg-zinc-900/95 p-4 shadow-2xl"
                    style={{ top: vendorMenuPos.top, left: vendorMenuPos.left }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs uppercase tracking-wide text-zinc-400">Select Vendors</span>
                      <div className="space-x-3 text-xs">
                        <button
                          onClick={() => setSelectedVendors([...ALL_VENDORS])}
                          className="text-amber-300 hover:underline"
                          type="button"
                        >
                          All
                        </button>
                        <button
                          onClick={() => setSelectedVendors([])}
                          className="text-amber-300 hover:underline"
                          type="button"
                        >
                          Clear
                        </button>
                      </div>
                    </div>

                    {/* Search */}
                    <div className="mb-3">
                      <input
                        value={vendorQuery}
                        onChange={(e) => setVendorQuery(e.target.value)}
                        placeholder="Search vendors"
                        className="w-full rounded-lg bg-zinc-900/60 border border-white/10 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-amber-400/70"
                      />
                    </div>

                    {/* Grid of vendor pills */}
                    <div className="grid grid-cols-2 gap-2 max-h-64 overflow-auto pr-1">
                      {ALL_VENDORS
                        .filter(v => !vendorQuery || v.toLowerCase().includes(vendorQuery.toLowerCase()))
                        .map(v => {
                          const active = selectedVendors.includes(v);
                          return (
                            <button
                              key={v}
                              type="button"
                              onClick={() => toggleVendor(v)}
                              aria-pressed={active}
                              className={
                                'group flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm transition ' +
                                (active
                                  ? 'bg-amber-500/20 text-amber-200 border border-amber-400/40'
                                  : 'bg-white/5 hover:bg-white/10 text-zinc-200 border border-white/10')
                              }
                            >
                              <span className="truncate">{v}</span>
                              <span
                                className={
                                  'inline-flex h-4 w-4 items-center justify-center rounded-sm border text-[10px] ' +
                                  (active ? 'bg-amber-400 text-zinc-900 border-amber-300' : 'border-white/30')
                                }
                                aria-hidden
                              >
                                {active ? '✓' : ''}
                              </span>
                            </button>
                          );
                        })}
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-xs text-zinc-400">{selectedVendors.length} selected</span>
                      <button onClick={() => setVendorMenuOpen(false)} className={btnOutline} type="button">
                        Done
                      </button>
                    </div>
                  </div>
                ), document.body)}
              </>
            )}
          </div>
        </div>

        {error && <p className="mt-3 text-sm text-red-300">{error}</p>}
      </section>

      {/* Section: Editable Items (Model, Description, Vendor, Item/Query) */}
      {editableItems && (
        <section className={`${card} p-6 space-y-4`}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-amber-300">Review Items</h3>
            <button type="button" onClick={addRow} className={btnOutline}>
              + Add Row
            </button>
          </div>

          <div className={tableWrap}>
            <table className="min-w-full text-sm text-zinc-200">
              <thead className={thead}>
                <tr>
                  <th className={`${cell} text-left`}>Item</th>
                  <th className={cell}>Qty</th>
                  <th className={`${cell} text-left`}>Client Prompt (optional)</th>
                  <th className={cell}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {editableItems.map((row, idx) => (
                  <tr key={idx} className="border-t border-white/10">
                    <td className={cell}>
                      <input
                        value={row.item || ''}
                        onChange={(e) => {
                          const copy = [...editableItems];
                          copy[idx].item = e.target.value;
                          setEditableItems(copy);
                        }}
                        className={input}
                        placeholder={'e.g., 2x4" wood studs @ 16" o.c'}
                      />
                    </td>
                    <td className={cell}>
                      <div className="max-w-[6rem]">
                        <input
                          value={row.quantity ?? ''}
                          onChange={(e) => {
                            const copy = [...editableItems];
                            copy[idx].quantity = cleanQuantityInput(e.target.value);
                            setEditableItems(copy);
                          }}
                          onBlur={() => {
                            const copy = [...editableItems];
                            const normalized = normalizeQuantityString(copy[idx].quantity);
                            const nextValue = normalized || '1';
                            if (copy[idx].quantity !== nextValue) {
                              copy[idx].quantity = nextValue;
                              setEditableItems(copy);
                            }
                          }}
                          className={input}
                          inputMode="decimal"
                          placeholder="1"
                          aria-label="Quantity"
                        />
                      </div>
                    </td>
                    <td className={cell}>
                      <input
                        value={row.clientPrompt || ''}
                        onChange={(e) => {
                          const copy = [...editableItems];
                          copy[idx].clientPrompt = e.target.value;
                          setEditableItems(copy);
                        }}
                        className={input}
                        placeholder="Preferences/constraints to guide selection"
                      />
                    </td>
                    <td className={cell}>
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeRow(idx)}
                          className={btnOutline}
                          aria-label="Remove row"
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mb-4 flex flex-col gap-2 text-sm text-zinc-400 sm:flex-row sm:items-center sm:justify-between">
            <span>
              {creditQuote.loading
                ? 'Calculating credit requirement...'
                : `Costs ${creditsNeeded} credit${creditsNeeded === 1 ? '' : 's'}. Available: ${creditBalanceAvailable}.`}
            </span>
            {creditQuote.shortfall > 0 && (
              <span className="text-red-400">
                Need {creditQuote.shortfall} more credit{creditQuote.shortfall === 1 ? '' : 's'}.
              </span>
            )}
            {!creditQuote.loading && !creditQuote.shortfall && creditQuote.error && (
              <span className="text-red-400">{creditQuote.error}</span>
            )}
          </div>

          <button onClick={confirmAndFetchEstimate} disabled={disableConfirm} className={btnPrimary}>
            {confirming
              ? 'Fetching prices...'
              : `Confirm & Continue - ${creditsNeeded} credit${creditsNeeded === 1 ? '' : 's'}`}
          </button>
        </section>

      )}
      {pricingProgress && !pricingProgress.cancelled && pricingProgress.total && pricingProgress.completed < pricingProgress.total && (
        <section className={`${card} p-6 space-y-4`}>
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-lg font-semibold text-amber-300">Pricing Progress</h3>
            <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-zinc-400">
              <span>
                {pricingProgress.completed}/{pricingProgress.total} complete
              </span>
              {!pricingProgress.cancelled && pricingProgress.completed < pricingProgress.total && (
                <button
                  type="button"
                  onClick={handleCancelPricing}
                  disabled={disableCancel}
                  className={`${btnOutline} ${disableCancel ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  {cancelling ? 'Cancelling...' : 'Cancel'}
                </button>
              )}
            </div>
          </div>

          <div className="relative h-2 w-full overflow-hidden rounded-full bg-zinc-800">
            <div
              className="absolute h-full w-1/3 -translate-x-full transform bg-amber-300/80"
              style={{ animation: 'pricing-loading-bar 1.2s linear infinite' }}
            />
          </div>
          <p className="text-xs text-amber-200">Processing request - please do not refresh this page.</p>
        </section>
      )}

      {/* Section: Result */}
      {result && (
        <section className={`${card} p-6 space-y-6`}>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-amber-300">Result</h3>
              <p className="text-lg">
                <strong>Grand Total:</strong>{' '}
                <span className="text-amber-300">${grandTotal.toFixed(2)}</span>
              </p>
              <div className="mt-2 space-y-1 text-sm text-zinc-400">
                <div>Material: ${materialTotal.toFixed(2)}</div>
                {hasLaborFeature && <div>Labor: ${laborTotal.toFixed(2)}</div>}
              </div>
            </div>
            {hasLaborFeature && (
              <div className="w-full rounded-2xl border border-white/10 bg-black/30 p-4 lg:w-auto lg:min-w-[260px]">
                <div className="flex items-center justify-between text-sm text-zinc-400">
                  <span>Labor summary</span>
                  {laborLoading && <span className="text-xs text-zinc-500">Loading…</span>}
                </div>
                {laborError && <p className="mt-2 text-xs text-red-400">{laborError}</p>}
                {laborEntriesDetailed.length > 0 ? (
                  <div className="mt-3 space-y-2 text-sm">
                    {laborEntriesDetailed.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between rounded-xl border border-white/10 bg-black/20 px-3 py-2"
                      >
                        <span className="text-zinc-100">{item.name}</span>
                        <span className="text-zinc-400">
                          {item.hours.toFixed(2)}h · ${item.cost.toFixed(2)}
                        </span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between rounded-xl border border-amber-400/30 bg-amber-500/10 px-3 py-2 font-semibold text-amber-100">
                      <span>Total</span>
                      <span>
                        {laborTotals.hours.toFixed(2)}h · ${laborTotals.cost.toFixed(2)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-zinc-500">
                    {hasLaborProfiles
                      ? 'Use the labor section below to add entries.'
                      : 'Add labor roles in the Admin Console to begin tracking hours.'}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className={tableWrap}>
            <table className="min-w-full text-sm text-zinc-200">
              <thead className={thead}>
                <tr>
                  <th className={`${cell} text-left`}>Item</th>
                  <th className={cell}>Qty</th>
                  <th className={cell}>Unit Cost</th>
                  <th className={cell}>Material Total</th>
                  <th className={cell}>Source</th>
                  <th className={cell}>Link</th>
                  <th className={`${cell} text-left`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {result.breakdown.map((r, i) => {
                  const baseKey = normalizeQueryKey(r.item);
                  const { bucket: optionsBucket, key: bucketKey } = findOptionsBucket(r.item);
                  const entryQuery = optionsBucket?.query || r.item;
                  const lookupKey = bucketKey || normalizeQueryKey(entryQuery);
                  const stateKey = lookupKey || baseKey || `row-${i}`;
                  const baseCandidates = optionsBucket?.candidates || [];
                  let rowCandidates =
                    baseCandidates.length > 0
                      ? baseCandidates.map((candidate) => ({
                          ...candidate,
                          raw: candidate.raw ? { ...candidate.raw } : candidate.raw,
                        }))
                      : (() => {
                          const aggregated =
                            aggregatedRef.current.get(lookupKey) ||
                            aggregatedRef.current.get(baseKey) ||
                            aggregatedRef.current.get(normalizeQueryKey(entryQuery));
                          if (aggregated) {
                            return [{ ...normalizeCandidateRecord(aggregated, entryQuery) }];
                          }
                          return [];
                        })();

                  rowCandidates = padOptionsWithSyntheticAlternatives(
                    rowCandidates,
                    { ...r, query: entryQuery },
                    3
                  );

                  const selectedIndex = getSelectedIndex(stateKey, rowCandidates, entryQuery);
                  const selectedCandidate =
                    selectedIndex >= 0 && selectedIndex < rowCandidates.length
                      ? rowCandidates[selectedIndex]
                      : null;
                  const hasAlternatives = rowCandidates.length > 1;
                  const isExpanded = !!expandedRows[stateKey];
                  const rowKey = stateKey;
                  const isEditing = editState?.rowIndex === i;
                  const rowLoading = inlineLoadingIndex === i;
                  const progressTotal = pricingProgress?.total ?? 0;
                  const progressCompleted = pricingProgress?.completed ?? 0;
                  const runFinished = !pricingProgress || pricingProgress.cancelled || progressCompleted >= progressTotal;
                  const hasRequestId = Boolean((activeRequestId || '').trim());
                  const canEditRow = Boolean(result) && runFinished && hasRequestId && !confirming && !cancelling && inlineLoadingIndex === null;
                  const materialTotalValue = Number(r.totalItemCost || 0);
                  const displayItem = r.label || r.item;

                  return (
                    <React.Fragment key={rowKey}>
                      <tr className="border-t border-white/10">
                        <td className={`${cell} text-zinc-100`}>
                          <div className="flex flex-col gap-1">
                            <div>
                              {r.refunded ? (
                                <span className="line-through text-zinc-500">{displayItem || '\u2014'}</span>
                              ) : (
                                displayItem || '\u2014'
                              )}
                              {r.refunded && (
                                <span className="ml-2 inline-block px-2 py-1 text-xs rounded-full bg-red-600 text-white">
                                  Refunded
                                </span>
                              )}
                            </div>
                            {selectedCandidate?.title && (
                              <div className="text-xs text-zinc-400">{selectedCandidate.title}</div>
                            )}
                            {r.refunded && r.refundReason && (
                              <div className="text-xs text-zinc-500">Reason: {r.refundReason}</div>
                            )}
                            {hasAlternatives && (
                              <button
                                type="button"
                                onClick={() => toggleRowOptions(stateKey)}
                                className="text-xs text-amber-300 underline-offset-2 hover:text-amber-200 hover:underline"
                              >
                                {isExpanded ? 'Hide options' : `Options (${rowCandidates.length})`}
                              </button>
                            )}
                          </div>
                        </td>
                        <td className={cell}>{r.quantity}</td>
                        <td className={cell}>${r.unitCost.toFixed(2)}</td>
                        <td className={cell}>${materialTotalValue.toFixed(2)}</td>
                        <td className={`${cell} text-zinc-400`}>{r.source || '\u2014'}</td>
                        <td className={cell}>
                          {r.product_link && isHttpUrl(r.product_link) ? (
                            <a
                              href={sanitizeExternalUrl(r.product_link)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-amber-300 hover:underline"
                            >
                              View
                            </a>
                          ) : (
                            '\u2014'
                          )}
                        </td>
                        <td className={`${cell} text-right`}>
                          {rowLoading ? (
                            <span className="text-xs text-amber-200">Re-searching...</span>
                          ) : isEditing ? (
                            <span className="text-xs uppercase text-amber-200">Editing</span>
                          ) : canEditRow ? (
                            <button
                              type="button"
                              onClick={() => beginInlineEdit(i, displayItem, r.quantity)}
                              className={`${btnOutline} whitespace-nowrap text-xs`}
                            >
                              Edit &amp; Search Again
                            </button>
                          ) : null}
                        </td>
                      </tr>
                      {isEditing && (
                        <tr className="border-t border-white/10 bg-black/30">
                          <td colSpan={tableColumnCount} className="py-4">
                            <div className="space-y-4">
                              <div className="grid gap-3 sm:grid-cols-2">
                                <div>
                                  <label className="mb-1 block text-xs uppercase tracking-wide text-zinc-400">
                                    Item Name
                                  </label>
                                  <input
                                    value={editState?.query ?? ''}
                                    onChange={(e) =>
                                      setEditState((prev) =>
                                        prev && prev.rowIndex === i
                                          ? { ...prev, query: e.target.value }
                                          : prev
                                      )
                                    }
                                    className={input}
                                    placeholder="Update item description"
                                    disabled={rowLoading}
                                  />
                                </div>
                                <div>
                                  <label className="mb-1 block text-xs uppercase tracking-wide text-zinc-400">
                                    Quantity
                                  </label>
                                  <input
                                    value={editState?.quantity ?? ''}
                                    onChange={(e) =>
                                      setEditState((prev) =>
                                        prev && prev.rowIndex === i
                                          ? { ...prev, quantity: cleanQuantityInput(e.target.value) }
                                          : prev
                                      )
                                    }
                                    onBlur={(e) =>
                                      setEditState((prev) =>
                                        prev && prev.rowIndex === i
                                          ? {
                                              ...prev,
                                              quantity:
                                                normalizeQuantityString(prev.quantity || e.target.value) || '1',
                                            }
                                          : prev
                                      )
                                    }
                                    className={input}
                                    inputMode="decimal"
                                    placeholder="1"
                                    disabled={rowLoading}
                                  />
                                </div>
                              </div>
                              <div className="flex flex-col gap-2 text-xs text-zinc-400 sm:flex-row sm:items-center sm:justify-between">
                                <span>Searching again costs 1 additional credit.</span>
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    className={`${btnOutline} text-xs`}
                                    onClick={handleInlineEditCancel}
                                    disabled={rowLoading}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    className={`${btnPrimary} text-xs`}
                                    onClick={handleInlineEditSubmit}
                                    disabled={rowLoading}
                                  >
                                    {rowLoading ? 'Re-searching...' : 'Confirm & Re-search'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      {isExpanded && rowCandidates.length > 0 && (
                        <tr className="border-t border-white/10 bg-black/40">
                          <td colSpan={tableColumnCount} className="py-3">
                            <div className="space-y-2">
                              {rowCandidates.map((candidate, candidateIdx) => {
                                const candidateSelected = candidateIdx === selectedIndex;
                                const candidatePriceDisplay =
                                  candidate.priceDisplay ||
                                  (Number.isFinite(candidate.priceValue)
                                    ? formatCurrencyValue(candidate.priceValue)
                                    : '-');
                                const candidateSource = candidate.source || candidate.vendor || '';
                                const candidateLink = sanitizeExternalUrl(candidate.product_link || candidate.url || '');
                                return (
                                  <div
                                    key={`${rowKey}-candidate-${candidateIdx}-${candidate.compareKey || 'opt'}`}
                                    className={`flex flex-col gap-2 rounded-lg border px-3 py-3 sm:flex-row sm:items-center sm:justify-between ${
                                      candidateSelected
                                        ? 'border-amber-400/60 bg-amber-500/10'
                                        : 'border-white/10 bg-black/30'
                                    }`}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-semibold text-amber-100 truncate">
                                        {candidate.title || candidate.query || 'Option'}
                                      </div>
                                  {candidateSource ? (
                                    <div className="text-xs text-amber-100/70">Source: {candidateSource}</div>
                                  ) : null}
                                    </div>
                                    <div className="mt-2 flex shrink-0 items-center gap-3 sm:mt-0">
                                      <span className="text-sm text-amber-100">
                              {candidatePriceDisplay || '-'}
                            </span>
                            {candidateLink ? (
                              <a
                                href={candidateLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-amber-200 hover:text-amber-100 text-sm"
                                        >
                                          Open
                                        </a>
                                      ) : (
                                        <span className="text-amber-200/40 text-sm">-</span>
                                      )}
                                      {candidateSelected ? (
                                        <span className="text-xs uppercase font-semibold text-amber-200">
                                          Selected
                                        </span>
                                      ) : (
                                        <button
                                          type="button"
                                          className="text-xs text-amber-300 underline-offset-2 hover:text-amber-200 hover:underline"
                                          onClick={() =>
                                            handleOptionSelect(
                                              stateKey || entryQuery,
                                              entryQuery,
                                              candidate,
                                              candidateIdx
                                            )
                                          }
                                        >
                                          Select
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap gap-3">
            <button onClick={handleSave} disabled={saving} className={btnPrimary}>
              {saving ? 'Saving...' : 'Save to Project'}
            </button>
            <button onClick={() => downloadCSV(result.breakdown, tableLabel)} className={btnOutline}>
              Export to CSV
            </button>
          </div>
        </section>
      )}
      {hasLaborFeature && (
        <section className={`${card} p-6 space-y-5`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-amber-300">Labor</h3>
              <p className="text-sm text-zinc-400">Track crew hours separately from materials.</p>
            </div>
            <button
              type="button"
              className={btnPrimary}
              onClick={addLaborEntry}
              disabled={!hasLaborProfiles}
            >
              {hasLaborProfiles ? 'Add labor entry' : 'Add roles in Admin Console'}
            </button>
          </div>

          {laborEntriesDetailed.length === 0 ? (
            <p className="text-sm text-zinc-500">
              {hasLaborProfiles
                ? 'No labor entries yet. Use the button above to add one.'
                : 'Enable labor roles in the Admin Console to start tracking crew hours.'}
            </p>
          ) : (
            <div className="space-y-4">
              {laborEntriesDetailed.map((entry) => (
                <div
                  key={entry.id}
                  className="space-y-3 rounded-2xl border border-white/10 bg-black/30 p-4"
                >
                  <div className="grid gap-3 md:grid-cols-3">
                    <select
                      className={input}
                      value={entry.roleId || ''}
                      onChange={(e) =>
                        updateLaborEntry(entry.id, { roleId: e.target.value || null })
                      }
                    >
                      <option value="">Select labor role</option>
                      {laborProfiles.map((profile) => (
                        <option key={profile.id} value={profile.id}>
                          {profile.name} ({Number(profile.hourly_rate || 0).toFixed(2)}/hr)
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="0"
                      step="0.25"
                      className={input}
                      value={entry.hours}
                      onChange={(e) =>
                        updateLaborEntry(entry.id, { hours: normalizeLaborHours(e.target.value) })
                      }
                      placeholder="Hours"
                    />
                    <div className="flex items-center justify-between rounded-xl border border-white/10 px-3 py-2 text-sm text-zinc-400">
                      <span>Rate</span>
                      <span>${entry.rate.toFixed(2)}/hr</span>
                    </div>
                  </div>
                  <textarea
                    className={`${input} min-h-[80px]`}
                    placeholder="Notes (optional)"
                    value={entry.notes}
                    onChange={(e) => updateLaborEntry(entry.id, { notes: e.target.value })}
                  />
                  <div className="flex items-center justify-between text-sm text-zinc-400">
                    <div>
                      Cost:{' '}
                      <span className="text-zinc-100">${entry.cost.toFixed(2)}</span>
                    </div>
                    <button
                      type="button"
                      className="text-xs text-red-400 hover:text-red-200"
                      onClick={() => removeLaborEntry(entry.id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between rounded-2xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm font-semibold text-amber-100">
                <span>Total labor</span>
                <span>
                  {laborTotals.hours.toFixed(2)}h · ${laborTotals.cost.toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}





